<script lang="ts">import { buildBannerPath, getBannerFallbackPath } from "../utils";
import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { locale, t, Verbiage } from "@infinitro/content";
import { isMobile, loggedIn, ModalDialog, pushModal, toSiteUrl, toTitleCase } from "@infinitro/global";
import { onMount } from "svelte";
let { banner, width = "100%", height = "auto", buttonContainerStyle = "", buttonStyle = "", showRightSideOfImage = false } = $props();
const BUTTON_VARIANTS = [
	"primary",
	"secondary",
	"tertiary",
	"accent",
	"transparent",
	"red"
];
/* ---------- derived fields (single source: banner) ---------- */
let title = $derived(banner?.title ?? "");
let titleHighlight = $derived(banner?.titleHighlight ?? "");
let secondPartTitle = $derived(banner?.secondPartTitle ?? "");
let description = $derived(banner?.description ?? "");
let button = $derived(banner?.button ?? false);
let buttonText = $derived(banner?.buttonText ?? "");
let buttonTextLoggedIn = $derived(banner?.buttonTextLoggedIn ?? "");
let url = $derived(banner?.url ?? "");
let center = $derived(banner?.center ?? false);
let showTitle = $derived(banner?.showTitle ?? true);
let onlyImage = $derived(banner?.onlyImage ?? true);
let noSignUp = $derived(banner?.noSignUp ?? false);
let desktopBannerSize = $derived(banner?.desktopBannerSize ?? "1400x279");
let mobileBannerSize = $derived(banner?.mobileBannerSize ?? "1080x405");
/* ---------- reactive state ---------- */
let bgEl = $state();
let useAlternative = $derived($stateUi.bannerSwiper.bannerSwiperStyle === "alternative");
let useUppercaseSectionTitles = $derived($stateUi.layout.useUppercaseSectionTitles ?? true);
function formatBannerLine(key, text) {
	if (!key) return "";
	return useUppercaseSectionTitles ? text : toTitleCase(text);
}
let bannerTitleDisplay = $derived(formatBannerLine(title, $t(title)));
let bannerTitleHighlightDisplay = $derived(formatBannerLine(titleHighlight, $t(titleHighlight)));
let bannerSecondPartDisplay = $derived(formatBannerLine(secondPartTitle, $t(secondPartTitle)));
let rawButtonVariant = $derived(Reflect.get($stateUi.bannerSwiper, "buttonVariant"));
let buttonVariant = $derived(BUTTON_VARIANTS.includes(rawButtonVariant) ? rawButtonVariant : "primary");
let canRedirect = $derived(onlyImage && ($loggedIn || noSignUp));
/* ---------- helpers ---------- */
const clickSignUp = () => pushModal(ModalDialog.Auth, { tab: "signup" });
const onlyImageAction = (e) => {
	if (!canRedirect) {
		e.preventDefault();
		e.stopPropagation();
		clickSignUp();
	}
};
const setBg = (primary, fallback) => {
	if (!bgEl) return;
	const el = bgEl;
	const img = new Image();
	img.onload = () => el.style.backgroundImage = `url(${primary})`;
	img.onerror = () => el.style.backgroundImage = `url(${fallback})`;
	img.src = primary;
};
/* ---------- deterministic art ---------- */
let bannerArt = $derived.by(() => {
	if (!onlyImage) return {
		image: "",
		imageMb: "",
		fallback: "",
		fallbackMb: ""
	};
	const variant = $loggedIn || noSignUp ? "default" : "signup";
	// locale folder if not English
	const loc = $locale !== "en" ? $locale : undefined;
	return {
		image: buildBannerPath(banner, false, variant, loc),
		imageMb: buildBannerPath(banner, true, variant, loc),
		fallback: getBannerFallbackPath(banner, false, variant),
		fallbackMb: getBannerFallbackPath(banner, true, variant)
	};
});
let selectedImage = $derived(bannerArt.image);
let selectedImageMb = $derived(bannerArt.imageMb);
let fallbackImage = $derived(bannerArt.fallback);
let fallbackImageMb = $derived(bannerArt.fallbackMb);
onMount(() => {
	setBg($isMobile ? selectedImageMb : selectedImage, $isMobile ? fallbackImageMb : fallbackImage);
});
</script>

<div
	class="banner"
	class:banner-alternative={useAlternative}
	style="width:{width};height:{height};"
>
	<div
		class="banner-content banner-image-ratio"
		class:banner-image-content-alternative={useAlternative}
		class:banner-image-ratio_27_10={$isMobile && mobileBannerSize === '1080x405'}
		class:banner-image-ratio_1_1={$isMobile && mobileBannerSize === '1080x1080'}
		class:banner-image-ratio_5_1={!$isMobile && desktopBannerSize === '1400x279'}
		class:banner-image-ratio_4_1={!$isMobile && desktopBannerSize === '1400x350'}
	>
		{#if canRedirect}
			<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- toSiteUrl builds a runtime site URL, same pattern as libs/tabs -->
			<a href={toSiteUrl(url, $loggedIn)} class="block" aria-label={bannerTitleDisplay || url}>
				<div
					class="banner-image-ratio"
					bind:this={bgEl}
					class:background-image={!$isMobile}
					class:banner-image-mobile={$isMobile}
					class:show-right-side-of-image={showRightSideOfImage}
					class:banner-image-alternative={useAlternative}
					class:banner-image-ratio_27_10={$isMobile && mobileBannerSize === '1080x405'}
					class:banner-image-ratio_1_1={$isMobile && mobileBannerSize === '1080x1080'}
					class:banner-image-ratio_5_1={!$isMobile && desktopBannerSize === '1400x279'}
					class:banner-image-ratio_4_1={!$isMobile && desktopBannerSize === '1400x350'}
				></div>
			</a>
		{:else}
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<!-- svelte-ignore a11y_no_static_element_interactions -->
			<div
				class="banner-image-ratio"
				bind:this={bgEl}
				class:background-image={!$isMobile}
				class:banner-image-mobile={$isMobile}
				class:show-right-side-of-image={showRightSideOfImage}
				class:banner-image-alternative={useAlternative}
				class:banner-image-ratio_27_10={$isMobile && mobileBannerSize === '1080x405'}
				class:banner-image-ratio_1_1={$isMobile && mobileBannerSize === '1080x1080'}
				class:banner-image-ratio_5_1={!$isMobile && desktopBannerSize === '1400x279'}
				class:banner-image-ratio_4_1={!$isMobile && desktopBannerSize === '1400x350'}
				onclick={onlyImage ? onlyImageAction : undefined}
			></div>
		{/if}

		{#if !onlyImage}
			<div class="text-content {center && 'text-content-center'}">
				<div class="banner-title">
					{#if showTitle}
						<h3 class="title" class:title-uppercase={useUppercaseSectionTitles}>
							{bannerTitleDisplay}
						</h3>
					{/if}
					<h3
						class="title highlight"
						class:title-uppercase={useUppercaseSectionTitles}
						class:not-show-title={$t(title as VerbiageKey).trim() === '\u200B'}
					>
						{bannerTitleHighlightDisplay}
					</h3>
					<h3 class="title" class:title-uppercase={useUppercaseSectionTitles}>
						{bannerSecondPartDisplay}
					</h3>
				</div>

				{#if description}
					<p class="description">{$t(description as VerbiageKey)}</p>
				{/if}

				{#if button}
					<div class="button-container" style={buttonContainerStyle}>
						{#if $loggedIn}
							<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- dynamic player route, same pattern as libs/tabs -->
							<a href={`/player${url}`}>
								<Button
									text={$t(buttonTextLoggedIn as VerbiageKey)}
									variant={buttonVariant}
									styleCustom={buttonStyle}
									minWidth
								/>
							</a>
						{:else}
							<Button
								text={$t(buttonText as VerbiageKey)}
								variant={buttonVariant}
								styleCustom={buttonStyle}
								minWidth
								onClick={clickSignUp}
							/>
						{/if}
					</div>
				{/if}
			</div>
		{/if}
	</div>
</div>

<style>
	.banner {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 16px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}

	.banner-content {
		position: relative;
		min-height: 160px;
		width: 100%;
}

	@media (min-width: 1024px) {

		.banner-content {
				min-height: 248px;
		}
}

	.background-image,
	.banner-image-mobile {
		position: fixed;
		top: 0px;
		left: 0px;
		height: 160px;
		width: 100%;
		border-radius: 16px;
		background-size: cover;
		-o-object-fit: cover;
		   object-fit: cover;
}

	@media (min-width: 1024px) {

		.background-image,
	.banner-image-mobile {
				height: 248px;
		}
}

	.background-image,
	.banner-image-mobile {
		background-position: right center;
	}

	.show-right-side-of-image {
		background-position: right bottom;
		background-repeat: no-repeat;
}

	@media (min-width: 768px) {

		.show-right-side-of-image {
				background-position: right;
				background-repeat: no-repeat;
		}
}

	.text-content {
		width: 100%;
		padding: 0.75rem;
}

	@media (min-width: 1024px) {

		.text-content {
				padding: 1.5rem;
		}
}

	.banner-title {
		display: flex;
		gap: 0.5rem;
}

	.title {
		margin-bottom: 0px;
		text-align: start;
		font-size: 16px;
		font-weight: 800;
}

	@media (min-width: 1024px) {

		.title {
				font-size: 40px;
				line-height: 40px;
		}
}

	.title-uppercase {
		text-transform: uppercase;
}

	.highlight {
		color: var(--color-primary-500);
	}

	.description {
		margin-top: 0px;
		margin-bottom: 0.25rem;
		text-align: start;
		font-size: 10px;
		font-weight: 100;
}

	@media (min-width: 1024px) {

		.description {
				font-size: 18px;
		}
}

	.description {
		white-space: pre-wrap;
	}

	.text-content {
		position: absolute;
		top: 0px;
		left: 0px;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		gap: 0.5rem;
}

	.text-content-center {
		align-items: center;
}

	.not-show-title {
		margin-left: -8px;
}

	.banner-alternative {
		border-radius: 0px;
}

	.banner-image-content-alternative,
	.banner-image-alternative {
		height: auto;
		max-height: 100%;
		min-height: 0px;
		width: 100%;
		cursor: pointer;
		border-radius: 0px;
}
	.banner-image-ratio_27_10 {
		aspect-ratio: 2.7/1;
}
	.banner-image-ratio_1_1 {
		aspect-ratio: 1/1;
}
	.banner-image-ratio_5_1 {
		aspect-ratio: 5/1;
}
	.banner-image-ratio_4_1 {
		aspect-ratio: 4/1;
}
</style>
